import React from 'react';
import moment from 'moment';
import * as toastMessages from '../../../core/toastMessages';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import UploadSection from '../../../core/components/UploadSection';
import { RequiredWrapper } from '../../../core/formValidation';
import {
  addEmailRecipient,
  appendSiteSpecificInformation,
  getRenderedEmailTemplateResponse,
  getResetEmailState,
  removeEmailRecipient,
} from '../utils/claimDrawerHelpers';
import { DEFAULT_ESTIMATES_COUNT } from '../models';

const getTemplateDisplayName = (template) => {
  const key = `${template?.key || template?.type || template?.name || ''}`;

  if (/customerclaimmessage|opened/i.test(key)) return 'Claim Opened';
  if (/investigating/i.test(key)) return 'Claim Investigating';
  if (/designatedrepairshop/i.test(key)) return 'Estimates Required - Preferred Body Shop';
  if (/repairapproval/i.test(key)) return 'Estimates Required';
  if (/approved|approval/i.test(key)) return 'Claim Approved';
  if (/denied|denial/i.test(key)) return 'Claim Denied';
  if (/completed/i.test(key)) return 'Claim Completed';

  return `${template?.name || key}`.replace(/^email\s*/i, '');
};

// The Denied email no longer includes inspection findings; stored templates may still have the
// "including [inspection findings/...]," placeholder, so drop it from the sentence.
const removeInspectionFindingsPlaceholder = (template) =>
  `${template || ''}`.replace(
    /including\s*(?:<strong>\s*)?\[inspection findings[^\]]*\]\s*(?:<\/strong>)?\s*,\s*/gi,
    ''
  );

const replaceApprovedClaimPlaceholders = (
  template,
  { repairShopName, approvedRepairs, approvedAmount }
) => {
  let nextTemplate = `${template || ''}`;

  if (repairShopName) {
    nextTemplate = nextTemplate.replace(/\[Repair Shop Name\]/g, repairShopName);
  }

  if (approvedRepairs) {
    nextTemplate = nextTemplate.replace(/\[list specific repairs\]/gi, approvedRepairs);
  }

  if (approvedAmount) {
    nextTemplate = nextTemplate
      .replace(/\$\[total amount\]/gi, `$${approvedAmount}`)
      .replace(/\[total amount\]/gi, approvedAmount);
  }

  return nextTemplate;
};

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

// The estimates required template asks for "three estimates"; swap in the chosen count.
const replaceEstimatesCount = (template, estimatesCount) => {
  const count = parseInt(estimatesCount, 10);
  if (!Number.isInteger(count) || count < 1) return `${template || ''}`;

  const countText = NUMBER_WORDS[count] || `${count}`;
  return `${template || ''}`.replace(
    /\bthree (repair )?estimates\b/gi,
    (match, repair = '') => `${countText} ${repair}${count === 1 ? 'estimate' : 'estimates'}`
  );
};

const formatCurrencyInput = (value) => {
  const normalized = `${value ?? ''}`.replace(/[^\d.]/g, '');
  if (!normalized) return '';

  const [whole = '', ...decimalParts] = normalized.split('.');
  const decimals = decimalParts.join('').slice(0, 2);

  return normalized.includes('.') ? `${whole}.${decimals}` : whole;
};

const formatAttachmentLabel = (file) => {
  const fileName = file?.originalName || file?.name || `File ${file?.id}`;
  const mimeType = file?.mimeType ? ` (${file.mimeType})` : '';
  return `${fileName}${mimeType}`;
};

const normalizeEmailPreviewBody = (html) => {
  if (!html || typeof document === 'undefined') return html || '';

  const container = document.createElement('div');
  container.innerHTML = html;

  const emailContent = container.querySelector('.email-content');
  if (emailContent) return emailContent.innerHTML;

  const contentWrap = container.querySelector('.content-wrap');
  if (contentWrap) return contentWrap.innerHTML;

  return html;
};

const ClaimEmailPanel = ({
  reclamation,
  canSendEmail,
  isClaimEmailOptedOut,
  emailRecipients,
  setEmailRecipients,
  emailInput,
  setEmailInput,
  emailTemplate,
  setEmailTemplate,
  initialEstimatesCount,
  initialDesignatedRepairShop,
  emailTemplates,
  isLoadingEmailTemplates,
  isLoadingEmailBody,
  loadedEmailSubject,
  loadedEmailHtml,
  emailTemplateResponse,
  setActiveCustomerAction,
  primaryRecipient,
  sendEmailSiteId,
  operatorGroupPayload,
  venuePayload,
  refreshReclamation,
  sendReclamationEmail,
  showEmailHistory,
  setShowEmailHistory,
  emailHistoryItems,
  emailBody,
  addFile,
}) => {
  const [repairShopName, setRepairShopName] = React.useState('');
  const [approvedAmount, setApprovedAmount] = React.useState('');
  const [approvedRepairs, setApprovedRepairs] = React.useState('');
  const [denialReason, setDenialReason] = React.useState('');
  const [resolution, setResolution] = React.useState('');
  const [additionalNotes, setAdditionalNotes] = React.useState('');
  const [estimatesCount, setEstimatesCount] = React.useState(
    `${initialEstimatesCount || DEFAULT_ESTIMATES_COUNT}`
  );
  const [repairShopAddress, setRepairShopAddress] = React.useState(
    initialDesignatedRepairShop?.address || ''
  );
  const [selectedAttachmentFileIds, setSelectedAttachmentFileIds] = React.useState([]);
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const isDenialTemplate = /denied|denial/i.test(emailTemplate || '');
  const isResolvedTemplate = /resolved/i.test(emailTemplate || '');
  const isFinalApprovalTemplate = /finalapproval/i.test(emailTemplate || '');
  const isRepairApprovalTemplate = /repairapproval/i.test(emailTemplate || '');
  const isDesignatedRepairShopTemplate = /designatedrepairshop/i.test(emailTemplate || '');
  const hasEstimatesCount = parseInt(estimatesCount, 10) > 0;
  const hasDesignatedRepairShopFields =
    !!`${repairShopName || ''}`.trim() && !!`${repairShopAddress || ''}`.trim();
  const attachmentFiles = React.useMemo(
    () =>
      Array.isArray(reclamation?.files)
        ? reclamation.files.filter((file) => !file?.archivedAt)
        : [],
    [reclamation]
  );
  const hasApprovalFields =
    !!`${repairShopName || ''}`.trim() &&
    !!`${approvedRepairs || ''}`.trim() &&
    !!`${approvedAmount || ''}`.trim();
  const canSendCurrentEmail =
    canSendEmail &&
    !isClaimEmailOptedOut &&
    (!isResolvedTemplate || !!`${resolution || ''}`.trim()) &&
    (!isFinalApprovalTemplate || hasApprovalFields) &&
    (!isRepairApprovalTemplate || hasEstimatesCount) &&
    (!isDesignatedRepairShopTemplate || hasDesignatedRepairShopFields);
  const templateFields = {
    repairShopName,
    approvedAmount,
    approvedRepairs,
    denialReason,
    resolution,
    additionalNotes,
    estimatesCount,
    repairShopAddress,
  };
  const renderedTemplatePreview = React.useMemo(
    () =>
      getRenderedEmailTemplateResponse(emailTemplateResponse, emailTemplate, {
        reclamation,
        venue: venuePayload,
        operatorGroup: operatorGroupPayload,
        templateFields,
      }),
    [
      emailTemplateResponse,
      emailTemplate,
      reclamation,
      venuePayload,
      operatorGroupPayload,
      repairShopName,
      approvedAmount,
      approvedRepairs,
      denialReason,
      resolution,
      additionalNotes,
      estimatesCount,
      repairShopAddress,
    ]
  );
  const approvedEmailBody = (() => {
    const baseTemplate =
      renderedTemplatePreview.loadedEmailHtml || loadedEmailHtml || emailBody || '';

    if (isDenialTemplate) {
      return removeInspectionFindingsPlaceholder(baseTemplate);
    }

    if (isFinalApprovalTemplate) {
      return replaceApprovedClaimPlaceholders(baseTemplate, {
        repairShopName,
        approvedRepairs,
        approvedAmount,
      });
    }

    if (isRepairApprovalTemplate) {
      return replaceEstimatesCount(baseTemplate, estimatesCount);
    }

    return baseTemplate;
  })();
  const resolvedEmailBody = appendSiteSpecificInformation(approvedEmailBody, additionalNotes);
  const resolvedEmailPreviewBody = React.useMemo(
    () => normalizeEmailPreviewBody(resolvedEmailBody),
    [resolvedEmailBody]
  );

  const resolvedEmailSubject = (() => {
    const baseSubject =
      renderedTemplatePreview.loadedEmailSubject ||
      loadedEmailSubject ||
      reclamation?.emailSubject ||
      '';

    if (isFinalApprovalTemplate) {
      return replaceApprovedClaimPlaceholders(baseSubject, {
        repairShopName,
        approvedRepairs,
        approvedAmount,
      });
    }

    if (isRepairApprovalTemplate) {
      return replaceEstimatesCount(baseSubject, estimatesCount);
    }

    return baseSubject;
  })();

  const resetDecisionFields = React.useCallback(() => {
    setRepairShopName(reclamation?.repairShopName || initialDesignatedRepairShop?.name || '');
    setApprovedAmount(formatCurrencyInput(reclamation?.approvedAmount ?? reclamation?.cost ?? ''));
    setApprovedRepairs(reclamation?.approvedRepairs || '');
    setDenialReason(reclamation?.denialReason || '');
    setResolution(reclamation?.resolution || '');
    setAdditionalNotes(reclamation?.additionalNotes || '');
    setEstimatesCount(`${initialEstimatesCount || DEFAULT_ESTIMATES_COUNT}`);
    setRepairShopAddress(initialDesignatedRepairShop?.address || '');
    setSelectedAttachmentFileIds([]);
  }, [reclamation, initialEstimatesCount, initialDesignatedRepairShop]);

  React.useEffect(() => {
    resetDecisionFields();
  }, [resetDecisionFields, emailTemplate]);

  const handleCancel = () => {
    const nextState = getResetEmailState(reclamation, emailTemplates, loadedEmailHtml);
    setActiveCustomerAction(nextState.activeCustomerAction);
    setEmailRecipients(nextState.emailRecipients);
    setEmailInput(nextState.emailInput);
    setEmailTemplate(nextState.emailTemplate);
    resetDecisionFields();
  };

  const handleSendEmail = () => {
    if (!canSendCurrentEmail || !sendEmailSiteId || isSendingEmail) return;

    setIsSendingEmail(true);
    sendReclamationEmail({
      siteId: sendEmailSiteId,
      email: {
        id: reclamation.id,
        templateKey: emailTemplate,
        operatorGroup: operatorGroupPayload,
        recipientEmail: primaryRecipient,
        subject: resolvedEmailSubject,
        customBody: resolvedEmailBody,
        attachmentFileIds: selectedAttachmentFileIds,
        repairShopName,
        repairShopAddress,
        approvedAmount,
        approvedRepairs,
        denialReason,
        resolution,
        additionalNotes,
        venue: venuePayload,
      },
      callback: () => {
        toastMessages.generateToastr('success', 'Email Sent', `Email sent to ${primaryRecipient}`);
        refreshReclamation();
        setActiveCustomerAction(null);
        setIsSendingEmail(false);
      },
      errorCallback: (error) => {
        if (!error?.statusCode && !error?.responseStatus) {
          toastMessages.apiError(error);
        }
        setIsSendingEmail(false);
      },
    });
  };

  return (
    <>
      <div className="drawer-email">
        <div className="drawer-email-title">Draft Email</div>
        <label className="drawer-email-label" htmlFor="emailTo">
          To
        </label>
        <div className="drawer-email-to form-control" id="emailTo">
          {emailRecipients.map((email) => (
            <span key={email} className="drawer-email-chip">
              {email}
              <button
                type="button"
                className="drawer-email-chip-remove"
                onClick={() => setEmailRecipients((prev) => removeEmailRecipient(prev, email))}
                aria-label={`Remove ${email}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            className="drawer-email-input"
            type="email"
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
            placeholder="Add email"
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ',') return;
              event.preventDefault();
              const nextRecipients = addEmailRecipient(emailRecipients, emailInput);
              if (!nextRecipients.added) return;
              setEmailRecipients(nextRecipients.recipients);
              setEmailInput(nextRecipients.nextInput);
            }}
            onBlur={() => {
              const nextRecipients = addEmailRecipient(emailRecipients, emailInput);
              if (!nextRecipients.added) return;
              setEmailRecipients(nextRecipients.recipients);
              setEmailInput(nextRecipients.nextInput);
            }}
          />
        </div>
        <label className="drawer-email-label" htmlFor="emailTemplate">
          Template
        </label>
        <select
          id="emailTemplate"
          className="form-control drawer-email-input"
          value={emailTemplate}
          onChange={(event) => setEmailTemplate(event.target.value)}
          disabled={isLoadingEmailTemplates || emailTemplates.length === 0}
        >
          {emailTemplates.length === 0 ? (
            <option value="">
              {isLoadingEmailTemplates ? 'Loading templates...' : 'No templates available'}
            </option>
          ) : (
            emailTemplates.map((template) => (
              <option
                key={template.key || template.type || template.name}
                value={template.key || template.type || template.name}
              >
                {getTemplateDisplayName(template)}
              </option>
            ))
          )}
        </select>
        <label className="drawer-email-label" htmlFor="emailPreviewSubject">
          Subject
        </label>
        <div id="emailPreviewSubject" className="form-control drawer-email-input" aria-readonly>
          {resolvedEmailSubject || 'No subject provided'}
        </div>
        {isRepairApprovalTemplate && (
          <>
            <label
              className="drawer-email-label drawer-email-label-required"
              htmlFor="estimatesCount"
            >
              Number of Estimates *
            </label>
            <RequiredWrapper value={estimatesCount} exclusions={['']}>
              <input
                id="estimatesCount"
                className="form-control drawer-email-input"
                type="number"
                min={1}
                value={estimatesCount}
                onChange={(event) => setEstimatesCount(event.target.value)}
                required
              />
            </RequiredWrapper>
          </>
        )}
        {isDesignatedRepairShopTemplate && (
          <div className="drawer-email-grid">
            <div>
              <label
                className="drawer-email-label drawer-email-label-required"
                htmlFor="designatedRepairShopName"
              >
                Repair Shop Name *
              </label>
              <RequiredWrapper value={repairShopName} exclusions={['']}>
                <input
                  id="designatedRepairShopName"
                  className="form-control drawer-email-input"
                  type="text"
                  value={repairShopName}
                  onChange={(event) => setRepairShopName(event.target.value)}
                  placeholder="Repair shop name"
                  required
                />
              </RequiredWrapper>
            </div>
            <div>
              <label
                className="drawer-email-label drawer-email-label-required"
                htmlFor="designatedRepairShopAddress"
              >
                Repair Shop Address *
              </label>
              <RequiredWrapper value={repairShopAddress} exclusions={['']}>
                <input
                  id="designatedRepairShopAddress"
                  className="form-control drawer-email-input"
                  type="text"
                  value={repairShopAddress}
                  onChange={(event) => setRepairShopAddress(event.target.value)}
                  placeholder="Repair shop address"
                  required
                />
              </RequiredWrapper>
            </div>
          </div>
        )}
        {isFinalApprovalTemplate && (
          <>
            <div className="drawer-email-grid">
              <div>
                <label
                  className="drawer-email-label drawer-email-label-required"
                  htmlFor="repairShopName"
                >
                  Repair Shop Name *
                </label>
                <RequiredWrapper value={repairShopName} exclusions={['']}>
                  <input
                    id="repairShopName"
                    className="form-control drawer-email-input"
                    type="text"
                    value={repairShopName}
                    onChange={(event) => setRepairShopName(event.target.value)}
                    placeholder="Repair shop name"
                    required
                  />
                </RequiredWrapper>
              </div>
              <div>
                <label
                  className="drawer-email-label drawer-email-label-required"
                  htmlFor="approvedAmount"
                >
                  Total Approved Cost *
                </label>
                <RequiredWrapper value={approvedAmount} exclusions={['']}>
                  <input
                    id="approvedAmount"
                    className="form-control drawer-email-input"
                    type="text"
                    inputMode="decimal"
                    value={approvedAmount}
                    onChange={(event) => setApprovedAmount(formatCurrencyInput(event.target.value))}
                    placeholder="Total approved cost"
                    required
                  />
                </RequiredWrapper>
              </div>
            </div>
            <label
              className="drawer-email-label drawer-email-label-required"
              htmlFor="approvedRepairs"
            >
              Repairs *
            </label>
            <RequiredWrapper value={approvedRepairs} exclusions={['']}>
              <textarea
                id="approvedRepairs"
                className="form-control drawer-email-input"
                rows={3}
                value={approvedRepairs}
                onChange={(event) => setApprovedRepairs(event.target.value)}
                placeholder="Repairs"
                required
              />
            </RequiredWrapper>
          </>
        )}
        {isResolvedTemplate && (
          <>
            <label className="drawer-email-label drawer-email-label-required" htmlFor="resolution">
              Resolution *
            </label>
            <RequiredWrapper value={resolution} exclusions={['']}>
              <textarea
                id="resolution"
                className="form-control drawer-email-input"
                rows={3}
                value={resolution}
                onChange={(event) => setResolution(event.target.value)}
                placeholder="Enter the agreed-upon resolution"
                required
              />
            </RequiredWrapper>
          </>
        )}
        <label className="drawer-email-label" htmlFor="siteSpecificInformation">
          Site-Specific Information
        </label>
        <textarea
          id="siteSpecificInformation"
          className="form-control drawer-email-input"
          rows={4}
          value={additionalNotes}
          onChange={(event) => setAdditionalNotes(event.target.value)}
          placeholder="Add optional information specific to your site"
          maxLength={2000}
        />
        <div className="drawer-email-help">
          This information is appended to the approved template. The template body cannot be edited.
        </div>
        <label className="drawer-email-label" htmlFor="emailPreview">
          Email Preview
        </label>
        <div id="emailPreview" className="form-control drawer-email-preview">
          {isLoadingEmailBody ? (
            <span style={{ color: '#6b7280' }}>Loading email...</span>
          ) : (
            <div
              className="drawer-email-preview-content"
              dangerouslySetInnerHTML={{ __html: resolvedEmailPreviewBody }}
            />
          )}
        </div>
        <div className="drawer-email-upload">
          <label className="drawer-email-label">Attach File</label>
          <UploadSection
            fileType={['image', 'video', 'document']}
            addFile={addFile}
            buttonLabel="Attach File"
          />
        </div>
        {attachmentFiles.length > 0 && (
          <>
            <label className="drawer-email-label">Include As Attachments</label>
            <div className="drawer-email-attachments form-control">
              {attachmentFiles.map((file) => {
                const fileId = Number(file.id);
                const checked = selectedAttachmentFileIds.includes(fileId);
                return (
                  <label key={file.id} className="drawer-email-attachment-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => {
                        setSelectedAttachmentFileIds((prev) => {
                          if (event.target.checked) {
                            return prev.includes(fileId) ? prev : [...prev, fileId];
                          }
                          return prev.filter((id) => id !== fileId);
                        });
                      }}
                    />
                    <span>{formatAttachmentLabel(file)}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}
        <div className="drawer-email-actions">
          <button type="button" className="button inverted" onClick={handleCancel}>
            Cancel
          </button>
          <ButtonWithLoader
            style="button primary"
            disabled={!canSendCurrentEmail}
            isLoading={isSendingEmail}
            handleClick={handleSendEmail}
          >
            {isClaimEmailOptedOut ? 'Email Opted Out' : 'Send Email'}
          </ButtonWithLoader>
        </div>
      </div>
      <div className="drawer-action-history">
        <button
          type="button"
          className="button inverted drawer-action-history-toggle"
          onClick={() => setShowEmailHistory((prev) => !prev)}
        >
          {showEmailHistory ? 'Hide Email History' : 'Show Email History'}
        </button>
        {showEmailHistory &&
          (emailHistoryItems.length > 0 ? (
            <div className="drawer-action-history-list">
              {emailHistoryItems.slice(0, 5).map((item, index) => (
                <div key={item.id || item.createdAt || index} className="drawer-list-item">
                  <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    {item.createdAt ? moment(item.createdAt).format('MMM D, YYYY h:mm A') : ''}
                  </div>
                  <div className="drawer-action-history-detail">
                    {item.details?.subject || 'Your warranty request has been created.'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="drawer-action-history-empty">No email history yet.</div>
          ))}
      </div>
    </>
  );
};

export default ClaimEmailPanel;
